import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Volume2, VolumeX, Music, Music2, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '../../lib/utils';
interface SettingsScreenProps {
  isOpen: boolean;
  onClose: () => void;
  settings: GameSettings;
  onSave: (settings: GameSettings) => void;
}
export interface GameSettings {
  timePerRound: number; // seconds (10-90)
  soundEffects: boolean;
  music: boolean;
  zoomLevel: number; // 80-120%
}
export function SettingsScreen({
  isOpen,
  onClose,
  settings,
  onSave
}: SettingsScreenProps) {
  const [localSettings, setLocalSettings] = useState<GameSettings>(settings);
  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Settings" className="max-w-lg" data-id="element-628">
      <div className="space-y-6" data-id="element-629">
        {/* Zoom Level */}
        <Card variant="outline" padding="md" data-id="element-630">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-631">
            <ZoomIn size={20} data-id="element-632" />
            Display Zoom
          </h3>
          <div className="space-y-3" data-id="element-633">
            <div className="flex items-center justify-between" data-id="element-634">
              <span className="text-sm text-slate-600" data-id="element-635">Zoom level</span>
              <span className="text-lg font-bold text-slate-900" data-id="element-636">
                {localSettings.zoomLevel}%
              </span>
            </div>
            <input type="range" min="80" max="120" step="5" value={localSettings.zoomLevel} onChange={e => setLocalSettings({
            ...localSettings,
            zoomLevel: parseInt(e.target.value)
          })} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-637" />
            <div className="flex justify-between text-xs text-slate-400" data-id="element-638">
              <span data-id="element-639">80% (Zoom Out)</span>
              <span data-id="element-640">120% (Zoom In)</span>
            </div>
            <p className="text-xs text-slate-500" data-id="element-641">
              Adjust zoom to fit more content on screen or make elements larger
            </p>
          </div>
        </Card>

        {/* Timer Settings */}
        <Card variant="outline" padding="md" data-id="element-642">
          <h3 className="font-bold text-slate-900 mb-3" data-id="element-643">Timer Settings</h3>
          <div className="space-y-3" data-id="element-644">
            <div className="flex items-center justify-between" data-id="element-645">
              <span className="text-sm text-slate-600" data-id="element-646">Time per round</span>
              <span className="text-lg font-bold text-slate-900" data-id="element-647">
                {localSettings.timePerRound}s
              </span>
            </div>
            <input type="range" min="10" max="90" step="5" value={localSettings.timePerRound} onChange={e => setLocalSettings({
            ...localSettings,
            timePerRound: parseInt(e.target.value)
          })} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-648" />
            <div className="flex justify-between text-xs text-slate-400" data-id="element-649">
              <span data-id="element-650">10s</span>
              <span data-id="element-651">90s</span>
            </div>
          </div>
        </Card>

        {/* Audio Settings */}
        <Card variant="outline" padding="md" data-id="element-652">
          <h3 className="font-bold text-slate-900 mb-3" data-id="element-653">Audio</h3>
          <div className="space-y-3" data-id="element-654">
            {/* Sound Effects */}
            <button onClick={() => setLocalSettings({
            ...localSettings,
            soundEffects: !localSettings.soundEffects
          })} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-655">
              <div className="flex items-center gap-3" data-id="element-656">
                {localSettings.soundEffects ? <Volume2 className="text-blue-500" size={20} data-id="element-657" /> : <VolumeX className="text-slate-400" size={20} data-id="element-658" />}
                <div className="text-left" data-id="element-659">
                  <div className="font-semibold text-slate-900" data-id="element-660">
                    Sound Effects
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-661">
                    Dial scroll, clicks, counter tap
                  </div>
                </div>
              </div>
              <div className={cn('w-12 h-7 rounded-full transition-colors relative', localSettings.soundEffects ? 'bg-blue-500' : 'bg-slate-300')} data-id="element-662">
                <div className={cn('absolute top-1 w-5 h-5 bg-white rounded-full transition-transform', localSettings.soundEffects ? 'translate-x-6' : 'translate-x-1')} data-id="element-663" />
              </div>
            </button>

            {/* Music */}
            <button onClick={() => setLocalSettings({
            ...localSettings,
            music: !localSettings.music
          })} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-664">
              <div className="flex items-center gap-3" data-id="element-665">
                {localSettings.music ? <Music className="text-blue-500" size={20} data-id="element-666" /> : <Music2 className="text-slate-400" size={20} data-id="element-667" />}
                <div className="text-left" data-id="element-668">
                  <div className="font-semibold text-slate-900" data-id="element-669">
                    Background Music
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-670">Countdown loop</div>
                </div>
              </div>
              <div className={cn('w-12 h-7 rounded-full transition-colors relative', localSettings.music ? 'bg-blue-500' : 'bg-slate-300')} data-id="element-671">
                <div className={cn('absolute top-1 w-5 h-5 bg-white rounded-full transition-transform', localSettings.music ? 'translate-x-6' : 'translate-x-1')} data-id="element-672" />
              </div>
            </button>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3" data-id="element-673">
          <Button fullWidth variant="outline" onClick={onClose} data-id="element-674">
            Cancel
          </Button>
          <Button fullWidth onClick={handleSave} data-id="element-675">
            Save Settings
          </Button>
        </div>
      </div>
    </Modal>;
}